// Manages: households/{householdId}/budget (bills field)
import { write } from '@/lib/firebase';
import { todayStr } from '@/shared/utils/dateHelpers';

const budgetPath = (householdId) => `households/${householdId}/budget`;

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

export const billService = {
  async add(householdId, data, billForm) {
    const amt = parseFloat(billForm.amount);
    if (!billForm.name || isNaN(amt) || !billForm.category) return false;
    const bill = {
      id: uid(),
      name: billForm.name.trim(),
      amount: amt,
      category: billForm.category,
      frequency: billForm.frequency || 'Monthly',
      dueDate: billForm.dueDate || '',
      autoPay: !!billForm.autoPay,
      autoGenerate: billForm.autoGenerate !== false,
      createdAt: new Date().toISOString(),
      lastGeneratedDate: null,
      customSchedule: billForm.frequency === 'Custom' ? (billForm.customSchedule || null) : null,
    };
    await write(budgetPath(householdId), { ...data, bills: [...(data.bills || []), bill] });
    return true;
  },

  async update(householdId, data, billId, billForm) {
    const amt = parseFloat(billForm.amount);
    if (!billForm.name || isNaN(amt) || !billForm.category) return false;
    const orig = (data.bills || []).find(b => b.id === billId);
    const newFreq = billForm.frequency || 'Monthly';
    const newCustom = newFreq === 'Custom' ? (billForm.customSchedule || null) : null;
    // Resolve the dueDate to write: if the picker was left empty (user editing a legacy bill
    // without picking a new date), fall back to the stashed legacy string so it round-trips.
    const resolvedDueDate = billForm.dueDate || billForm._legacyDueDate || '';
    const scheduleChanged = orig && (
      orig.frequency !== newFreq ||
      (orig.dueDate || '') !== resolvedDueDate ||
      JSON.stringify(orig.customSchedule || null) !== JSON.stringify(newCustom)
    );
    const amountChanged = orig && orig.amount !== amt;
    const today = todayStr();

    let txns = data.txns || [];
    if (scheduleChanged) {
      txns = txns.filter(t => !(t.sourceBillId === billId && t.date >= today));
    } else if (amountChanged) {
      txns = txns.map(t =>
        t.sourceBillId === billId && t.date >= today ? { ...t, amount: amt } : t
      );
    }

    const bills = (data.bills || []).map(b => b.id === billId
      ? {
          ...b,
          name: billForm.name.trim(),
          amount: amt,
          category: billForm.category,
          frequency: newFreq,
          dueDate: resolvedDueDate,
          autoPay: !!billForm.autoPay,
          autoGenerate: billForm.autoGenerate !== false,
          customSchedule: newCustom,
          createdAt: b.createdAt || new Date().toISOString(),
          // Rewind lastGeneratedDate so future occurrences regenerate when schedule changes.
          lastGeneratedDate: scheduleChanged ? null : b.lastGeneratedDate,
        }
      : b);
    await write(budgetPath(householdId), { ...data, bills, txns });
    return true;
  },

  async remove(householdId, data, billId, cascade = false) {
    const bills = (data.bills || []).filter(b => b.id !== billId);
    const txns = cascade
      ? (data.txns || []).filter(t => t.sourceBillId !== billId)
      : (data.txns || []);
    await write(budgetPath(householdId), { ...data, bills, txns });
  },

  async updateLastGenerated(householdId, data, updates) {
    // updates: Map<billId, ISO date string>
    const bills = (data.bills || []).map(b => updates.has(b.id) ? { ...b, lastGeneratedDate: updates.get(b.id) } : b);
    await write(budgetPath(householdId), { ...data, bills });
  },
};
